import { useEffect, useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { MenuPosition } from "react-select";
import {
  Checkbox,
  DatePicker,
  Pill,
  RadioButton,
  Select,
  Table,
  Text,
  TextField,
  Tooltip,
} from "@foris/avocado-suite";
import { IGroupValue, ILeague } from "@modules/vacancies/contextVacancies/ContextVacancies";
import { InstructorLink } from "@models/ISchema";
import { getTotalBlocksPercent, isUnassignment } from "@modules/vacancies/utils";
import { useSettingsStore } from "@context/useSettingsStore";
import { useGetAssignationsTypesByLink } from "@modules/vacancies/hooks/useGetAssignationsTypesByLink";
import LinkRouter from "@common/components/link-router";
import useContextUrl from "@common/hooks/useContextUrl";
import useInstructorTableState from "../../hooks/useInstructorTableState";
import useInstructorTableInputsState from "../../hooks/useInstructorTableInputsState";
import {
  InstructorLinkTagsError,
  setInstructorCorrectionValidity,
  setAssignationTypesErrorsForLink,
  setInstructorLinkTagsErrors,
  setInstructorLinkValidity,
  useVacanciesStore,
} from "@modules/vacancies/contextVacancies/useVacanciesStore";
import { useInstructorRemovalFlowStore } from "@context/instructorRemovalFlow/useInstructorRemovalFlowStore";
import {
  INSTRUCTOR_LINK_TAG_KEYS,
  dateToIso,
  isoToDate,
  isoToDisplay,
  orderTagDefinitions,
} from "@modules/vacancies/Instructor/instructorLinkTags/utils";
import { TagDateError } from "@modules/vacancies/Instructor/instructorLinkTags/validation";
import { isCourseTypeForAssignationOrProgramming } from "@utils/courseUtils";
import {
  getAssignationDisabledTooltipKey,
  getAssignationSelectorDisabledReason,
  isUdcLimitModeEnabled,
} from "@utils/assignationLimits";
import styles from "./instructorsTable.module.scss";

interface InstructorsTableProps {
  instructors: InstructorLink[];
  linkTotalBlocks?: number;
  isEditing?: boolean;
  linkId: string;
  link: ILeague;
  onUpdateValues?: (values: IGroupValue[]) => void;
}

// Render the calendar in a body portal so it escapes the table's overflow:
// rows near the bottom edge were getting their calendar clipped. `portalId`
// keeps the dropdown anchored to the input (unlike `withPortal`, which centers
// it as a modal).
//
// floating-ui config:
// - `strategy: "fixed"` positions the portaled calendar against the viewport.
// - `transform: false` makes floating-ui position with top/left instead of a
//   CSS transform. Avocado's stylesheet forces `transform: none !important` on
//   `.react-datepicker-popper`, which would otherwise wipe out the translate
//   floating-ui injects and pin the calendar to the top-left corner.
const TAG_DATEPICKER_PORTAL_ID = "root-portal";
const TAG_DATEPICKER_CALENDAR_PROPS = {
  portalId: TAG_DATEPICKER_PORTAL_ID,
  popperProps: { strategy: "fixed" as const, transform: false },
};

const InstructorsTable = ({
  instructors = [],
  linkTotalBlocks = 0,
  isEditing = false,
  linkId,
  link,
  onUpdateValues,
}: InstructorsTableProps) => {
  const { workspace, origin, scenario } = useContextUrl();
  const ruleCourseTypeSetting = useSettingsStore(state => state.ruleCourseTypeSetting);
  const adaptedAssignmentsByCourseType = useSettingsStore(
    state => state.adaptedAssignmentsByCourseType,
  );
  const instructorLinkTagsSetting = useSettingsStore(state => state.instructorLinkTags);
  const removalStatus = useInstructorRemovalFlowStore(state => state.status);
  const affectedLinkId = useInstructorRemovalFlowStore(state => state.context?.linkId);
  const isCorrectionTarget =
    affectedLinkId === linkId && ["correcting-bundle", "saving-correction"].includes(removalStatus);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const isAssignationTypeEnabled = !!ruleCourseTypeSetting?.enabled;
  const courseType = link?.course?.courseType || null;
  const isValidCourseType = !!courseType && isCourseTypeForAssignationOrProgramming(courseType);

  const {
    options: assignationTypesOptions,
    blockingLimitStatus,
    isLoading: isLoadingAssignationTypes,
  } = useGetAssignationsTypesByLink({
    linkId,
    courseType,
    originId: origin,
    scenarioId: scenario,
    shouldFetch: isEditing && isAssignationTypeEnabled && isValidCourseType,
  });

  const assignationDisabledReason = getAssignationSelectorDisabledReason({
    isValidCourseType,
    blockingStatus: blockingLimitStatus,
  });
  const isUdcLimitMode = useMemo(
    () => isUdcLimitModeEnabled(adaptedAssignmentsByCourseType),
    [adaptedAssignmentsByCourseType],
  );
  const assignationDisabledTooltipKey = getAssignationDisabledTooltipKey({
    reason: assignationDisabledReason,
    isValidCourseType,
    isUdcLimitMode,
  });

  const {
    countResponsibility,
    updateResponsibilityValues,
    assignationType,
    updateAssignationType,
    someAssignationTypeInvalid,
    tagsByInstructorLink,
    updateTagValue,
    tagDateErrors,
    hasDropDate,
    isInstructorLinkTagsEnabled,
  } = useInstructorTableInputsState({
    instructors,
    linkTotalBlocks,
    linkId,
    link,
    isEditing,
    isAssignationTypeEnabled,
    onChange: onUpdateValues,
  });

  const orderedTagDefinitions = useMemo(
    () => (isInstructorLinkTagsEnabled ? orderTagDefinitions(instructorLinkTagsSetting?.tags) : []),
    [isInstructorLinkTagsEnabled, instructorLinkTagsSetting?.tags],
  );

  const tagLabelByKey = useMemo(
    () =>
      orderedTagDefinitions.reduce(
        (acc, def) => {
          acc[def.key] = def.label;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [orderedTagDefinitions],
  );

  const buildTagErrorMessage = (error: TagDateError) => {
    const field = (tagLabelByKey[error.key] ?? "").toLowerCase();
    const base = "bundle.link-card.instructor-table.tags.errors";

    switch (error.code) {
      case "out-of-period":
        // Dates contain "/", which i18next escapes to "&#x2F;" by default; disable
        // escaping for this interpolation (React still escapes on render).
        return t(`${base}.out-of-period`, {
          field,
          date: error.params.date,
          start: error.params.start,
          end: error.params.end,
          interpolation: { escapeValue: false },
        });
      case "payment-end-before-start":
        return t(`${base}.payment-end-before-start`);
      case "drop-before-assignment":
        return t(`${base}.drop-before-assignment`);
      case "assignment-after-payment-end":
        return t(`${base}.assignment-after-payment-end`);
      default:
        return "";
    }
  };

  const {
    orderInstructor,
    totalInstructorBlocks,
    totalInstructorBlocksPercent,
    totalResponsability,
    totalPayment,
    errors,
  } = useInstructorTableState({
    instructors,
    countResponsibility,
    linkTotalBlocks,
    linkId,
  });

  const instructorsIndex = useMemo(() => {
    return orderInstructor.reduce(
      (acc, item, index) => {
        acc[item.instructor.id] = index;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [orderInstructor]);

  const countResponsibilityById = useMemo(() => {
    return [...(countResponsibility ?? [])]?.reduce((acc, item) => {
      acc[item.instructor?.instructor?.id] = structuredClone(item);
      return acc;
    }, {});
  }, [countResponsibility]);

  const someResponsibilityInvalid = useMemo(() => {
    return Object.values(countResponsibilityById ?? {}).some((item: any) => {
      const teachingHoursPercentageInvalid =
        item.teachingHoursPercentage < 0 ||
        item.teachingHoursPercentage > 100 ||
        item.teachingHoursPercentage === "";
      const paymentPercentageInvalid =
        item.paymentPercentage < 0 || item.paymentPercentage > 100 || item.paymentPercentage === "";

      return teachingHoursPercentageInvalid || paymentPercentageInvalid;
    });
  }, [countResponsibilityById]);

  const primaryCount = useMemo(
    () =>
      (countResponsibility ?? []).filter(
        item => !isUnassignment(item?.instructor?.instructor?.id) && item.isPrimary,
      ).length,
    [countResponsibility],
  );

  useEffect(() => {
    const assignationTypeInvalid = isAssignationTypeEnabled
      ? someAssignationTypeInvalid?.length
      : 0;

    const tagGateActive = isInstructorLinkTagsEnabled && isEditing;
    const tagDatesInvalid = tagGateActive ? Object.keys(tagDateErrors).length > 0 : false;
    const sumInvalid = tagGateActive ? totalResponsability !== 100 || totalPayment !== 100 : false;
    const existingRulesValid =
      !someResponsibilityInvalid && !assignationTypeInvalid && !tagDatesInvalid && !sumInvalid;

    // Validity is kept per link: the Docentes tab mounts one table per link and
    // the save gate requires every link to be valid.
    setInstructorLinkValidity(linkId, existingRulesValid);

    if (isCorrectionTarget) {
      const responsibilityValid = totalResponsability === 100;
      const primaryValid = primaryCount === 1;
      setInstructorCorrectionValidity(linkId, {
        responsibilityValid,
        primaryValid,
        primaryCount,
        existingRulesValid,
        valid: responsibilityValid && primaryValid && existingRulesValid,
      });
    }

    return () => {
      setInstructorLinkValidity(linkId, null);
      if (isCorrectionTarget) setInstructorCorrectionValidity(linkId, null);
    };
  }, [
    someResponsibilityInvalid,
    someAssignationTypeInvalid,
    tagDateErrors,
    totalResponsability,
    totalPayment,
    isInstructorLinkTagsEnabled,
    isAssignationTypeEnabled,
    isEditing,
    isCorrectionTarget,
    primaryCount,
    linkId,
  ]);

  useEffect(() => {
    if (!isAssignationTypeEnabled) return;
    setAssignationTypesErrorsForLink(linkId, someAssignationTypeInvalid ?? []);

    return () => setAssignationTypesErrorsForLink(linkId, null);
  }, [isAssignationTypeEnabled, someAssignationTypeInvalid, linkId]);

  useEffect(() => {
    if (!isInstructorLinkTagsEnabled) return;

    const otherLinksErrors = useVacanciesStore
      .getState()
      .instructorLinkTagsErrors.filter(item => item.link?.linkId !== linkId);

    if (!isEditing) {
      setInstructorLinkTagsErrors(otherLinksErrors);
      return;
    }

    const messages: InstructorLinkTagsError[] = [];

    Object.values(tagDateErrors).forEach(errorsForInstructor => {
      errorsForInstructor.forEach(error => {
        let message = buildTagErrorMessage(error);

        // The cascade explanation reads as part of the baja-de-grupo error,
        // so it goes in the same paragraph as that field's date error.
        if (error.key === INSTRUCTOR_LINK_TAG_KEYS.dropDate) {
          message = `${message} ${t(
            "bundle.link-card.instructor-table.tags.drop-warning.message",
          )}`;
        }

        messages.push({ link, error: { type: "ERROR" as const, message } });
      });
    });

    // The 100% rule is its own separate error, always shown when the sum breaks.
    const sumInvalid = totalResponsability !== 100 || totalPayment !== 100;
    if (sumInvalid) {
      messages.push({
        link,
        error: {
          type: "ERROR" as const,
          message: t("bundle.link-card.instructor-table.tags.errors.sum-not-100"),
        },
      });
    }

    setInstructorLinkTagsErrors([...otherLinksErrors, ...messages]);
  }, [
    isInstructorLinkTagsEnabled,
    isEditing,
    tagDateErrors,
    totalResponsability,
    totalPayment,
    linkId,
    language,
  ]);

  return (
    <Table
      className={styles.instructorsTable}
      data={[
        ...orderInstructor,
        {
          isFooter: true,
        },
      ]}
      columns={[
        {
          key: "instructor",
          header: t("bundle.link-card.instructor-table.header.instructor"),
          renderCell: (data: any) => {
            if (data?.isFooter)
              return <Text weight="medium">{t("bundle.link-card.table-footer.total")}</Text>;

            const instructor = data as InstructorLink;
            const isUnassigment = isUnassignment(instructor?.instructor?.id);
            const hasAccess = instructor?.instructor?.hasAccess;
            const codeText = !isUnassigment ? instructor?.instructor?.code : "";

            return (
              <div className={styles.instructorsTable_instructor}>
                <RadioButton
                  checked={!!countResponsibilityById?.[instructor?.instructor?.id]?.isPrimary}
                  onChange={() =>
                    updateResponsibilityValues(
                      true,
                      linkId,
                      instructor?.instructor?.id,
                      "isPrimary",
                    )
                  }
                  disabled={
                    !isEditing ||
                    isUnassigment ||
                    (isInstructorLinkTagsEnabled && hasDropDate(instructor?.id))
                  }
                />
                {!isUnassigment && hasAccess ? (
                  <LinkRouter
                    colored
                    to={`/scheduler/editor/instructor/${workspace}/${scenario}/${origin}/${instructor?.id}`}
                  >
                    <span>{codeText}</span> {instructor?.instructor?.name}
                  </LinkRouter>
                ) : (
                  <Text>
                    {codeText ? `${codeText} ` : ""}
                    {instructor?.instructor?.name}
                  </Text>
                )}
              </div>
            );
          },
          desktopOptions: {
            headCellStyles: {
              width: "400px",
              minWidth: "80px",
            },
          },
        },
        {
          key: "totalBlocks",
          header: t("bundle.link-card.instructor-table.header.blocks"),
          renderCell: (data: any) => {
            if (data?.isFooter)
              return (
                <Text weight="medium">
                  {totalInstructorBlocks} ({totalInstructorBlocksPercent}%)
                </Text>
              );

            const instructor = data as InstructorLink;
            const blockPercent = instructor?.totalBlocks
              ? `${getTotalBlocksPercent(instructor?.totalBlocks, linkTotalBlocks)}`
              : "";

            return (
              <Text>{`${instructor?.totalBlocks} ${
                blockPercent ? `(${blockPercent}%)` : ""
              } `}</Text>
            );
          },
          desktopOptions: {
            headCellStyles: {
              width: "112px",
              minWidth: "80px",
            },
          },
        },
        {
          key: "responsability",
          header: t("bundle.link-card.instructor-table.header.responsibility"),
          renderCell: (data: any) => {
            if (data?.isFooter) {
              return <Text weight="medium">{totalResponsability}%</Text>;
            }

            const instructor = data as InstructorLink;
            const isDropped = isInstructorLinkTagsEnabled && hasDropDate(instructor?.id);
            const isDisabled =
              isUnassignment(instructor?.instructor?.id) || !isEditing || isDropped;
            const value =
              countResponsibilityById?.[instructor?.instructor?.id]?.teachingHoursPercentage ?? 0;

            return (
              <TextField
                className={styles.instructorsTable_inputField}
                type="text"
                value={value}
                onChange={e => {
                  const val =
                    e.target.value !== "" ? e.target.value.replace(/\D+/g, "") : e.target.value;

                  updateResponsibilityValues(
                    val as any,
                    linkId,
                    instructor?.instructor?.id,
                    "responsibility",
                  );
                }}
                disabled={isDisabled}
                statusDisplay="icon"
                status={
                  errors.responsabilityError || value < 0 || value > 100 ? "error" : undefined
                }
              />
            );
          },
          desktopOptions: {
            headCellStyles: {
              width: "132px",
              minWidth: "80px",
            },
          },
        },
        {
          key: "paymentPercentage",
          header: t("bundle.link-card.instructor-table.header.charge"),
          renderCell: (data: any) => {
            if (data?.isFooter) {
              return <Text weight="medium">{totalPayment}%</Text>;
            }

            const instructor = data as InstructorLink;
            const isDropped = isInstructorLinkTagsEnabled && hasDropDate(instructor?.id);
            const isDisabled =
              isUnassignment(instructor?.instructor?.id) || !isEditing || isDropped;
            const value =
              countResponsibilityById?.[instructor?.instructor?.id]?.paymentPercentage ?? 0;

            return (
              <TextField
                className={styles.instructorsTable_inputField}
                type="text"
                value={value}
                onChange={e => {
                  const val =
                    e.target.value !== "" ? e.target.value.replace(/\D+/g, "") : e.target.value;

                  updateResponsibilityValues(
                    val as any,
                    linkId,
                    instructor?.instructor?.id,
                    "payment",
                  );
                }}
                disabled={isDisabled}
                statusDisplay="icon"
                status={errors.paymentError || value < 0 || value > 100 ? "error" : undefined}
              />
            );
          },
          desktopOptions: {
            headCellStyles: {
              width: "96px",
              minWidth: "80px",
            },
          },
        },
        {
          key: "components",
          header: t("bundle.link-card.instructor-table.header.components"),
          renderCell: (data: any) => {
            if (data?.isFooter) return "";

            const instructor = data as InstructorLink;

            return (
              <Text>
                {instructor?.components?.length ? instructor?.components?.join(", ") : "-"}
              </Text>
            );
          },
          desktopOptions: {
            headCellStyles: {
              width: "112px",
              minWidth: "80px",
            },
          },
        },
        ...(isAssignationTypeEnabled
          ? [
              {
                key: "assignmentType",
                header: t("bundle.link-card.instructor-table.header.assignment-type"),
                renderCell: (data: any) => {
                  if (data?.isFooter) return "";

                  const instructorLink = data as InstructorLink;
                  const hasMoreThanOne = orderInstructor?.length > 1;
                  const isLastIndex =
                    instructorsIndex[instructorLink.instructor.id] === orderInstructor.length - 1;
                  const maxMenuHeight = !hasMoreThanOne ? 55 : 120;
                  const menuPosition = isLastIndex && hasMoreThanOne ? "top" : "bottom";

                  const isUnassignedRow = isUnassignment(instructorLink.instructor.id);
                  const assignationTypeValue = assignationType?.[instructorLink.instructor.id];

                  if (!isEditing) {
                    return assignationTypeValue?.data?.name ? (
                      <Pill>{assignationTypeValue?.data?.name}</Pill>
                    ) : (
                      "-"
                    );
                  }

                  // A policy reason disables every teacher of the link, including
                  // one that already holds the role (HU597).
                  const selector = (
                    <Select
                      disabled={
                        isUnassignedRow || !!assignationDisabledReason || isLoadingAssignationTypes
                      }
                      options={assignationTypesOptions}
                      value={assignationTypeValue}
                      onChange={selectedOption =>
                        updateAssignationType(
                          instructorLink?.instructor?.id,
                          instructorLink?.id,
                          selectedOption,
                        )
                      }
                      placeholder={t(
                        "bundle.link-card.instructor-table.body.rol-selector.placeholder",
                      )}
                      isLoading={isLoadingAssignationTypes}
                      maxMenuHeight={maxMenuHeight}
                      menuPosition={menuPosition as MenuPosition}
                      isClearable
                    />
                  );

                  if (isUnassignedRow || !assignationDisabledTooltipKey) return selector;

                  return (
                    <Tooltip
                      className={styles.instructorsTable_assignationTooltip}
                      label={
                        <span className={styles.instructorsTable_assignationTooltipLabel}>
                          {t(assignationDisabledTooltipKey)}
                        </span>
                      }
                      placement="top"
                    >
                      {selector}
                    </Tooltip>
                  );
                },
                desktopOptions: {
                  headCellStyles: {
                    width: isEditing ? "230px" : "142px",
                    minWidth: "80px",
                  },
                },
              },
            ]
          : []),
        ...orderedTagDefinitions.map(tagDef => ({
          key: `tag-${tagDef.key}`,
          header: tagDef.label,
          renderCell: (data: any) => {
            if (data?.isFooter) return "";

            const instructorLink = data as InstructorLink;
            if (isUnassignment(instructorLink?.instructor?.id)) return <Text>-</Text>;

            const instructorLinkId = instructorLink.id;
            const value = tagsByInstructorLink?.[instructorLinkId]?.[tagDef.key] ?? null;

            if (tagDef.value_type === "boolean") {
              return (
                <Checkbox
                  checked={Boolean(value)}
                  disabled={!isEditing}
                  onChange={e => updateTagValue(instructorLink, tagDef.key, e.target.checked)}
                />
              );
            }

            if (!isEditing) {
              return <Text>{isoToDisplay(value)}</Text>;
            }

            const fieldHasError = (tagDateErrors?.[instructorLinkId] ?? []).some(
              error => error.key === tagDef.key,
            );

            return (
              <DatePicker
                className={cx(styles.instructorsTable_datePicker, {
                  [styles.instructorsTable_datePicker__error]: fieldHasError,
                  [styles.instructorsTable_datePicker__hasValue]: !!value,
                })}
                placeholder={t("bundle.link-card.instructor-table.tags.date-placeholder")}
                selectedDate={isoToDate(value) ?? undefined}
                isClearable={Boolean(value)}
                calendarProps={TAG_DATEPICKER_CALENDAR_PROPS}
                onChange={(date: Date) =>
                  updateTagValue(instructorLink, tagDef.key, dateToIso(date))
                }
              />
            );
          },
          desktopOptions: {
            headCellStyles: {
              width: "132px",
              minWidth: "80px",
            },
          },
        })),
      ]}
    />
  );
};

export default InstructorsTable;
